import { resolveLanguage } from './copy/clientApiCopy';

// Keyed by the languages resolveLanguage can return.
const WEEKDAYS = {
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  es: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
};

const WEEKDAYS_FULL = {
  en: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  es: [
    'domingo',
    'lunes',
    'martes',
    'miércoles',
    'jueves',
    'viernes',
    'sábado',
  ],
};

const MONTHS = {
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  es: [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ],
};

/** A slot is bookable unless it is explicitly marked unavailable. */
export const isSlotAvailable = (slot) => slot?.available !== false;

const MONTHS_SHORT = {
  en: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ],
  es: [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic',
  ],
};

/** Reads the calendar day (YYYY-MM-DD) from an ISO-8601 slot timestamp. */
export const getSlotDayKey = (iso) => iso.slice(0, 10);

/** Reads the wall-clock time (HH:MM, 24h) from an ISO-8601 slot timestamp. */
export const getSlotTimeLabel = (iso) => iso?.slice(11, 16) ?? '';

/** Formats a slot's start and end as a range, e.g. "10:00 - 11:30". */
export const getSlotTimeRangeLabel = (start, end) =>
  `${getSlotTimeLabel(start)} - ${getSlotTimeLabel(end)}`;

/**
 * Splits a YYYY-MM-DD day key into the parts a date tile stacks:
 * short weekday, day of month, short month, e.g. { weekday: 'Mon', dayOfMonth: 30, month: 'Mar' }.
 */
export const getSlotDayTileParts = (dayKey, locale) => {
  const lang = resolveLanguage(locale);
  const date = new Date(`${dayKey}T00:00:00Z`);
  return {
    weekday: WEEKDAYS[lang][date.getUTCDay()],
    dayOfMonth: Number(dayKey.slice(8, 10)),
    month: MONTHS_SHORT[lang][date.getUTCMonth()],
  };
};

/** Short "day month" for one day key, e.g. "30 Mar" / "30 mar". */
const getShortDayMonth = (dayKey, lang) => {
  const dayOfMonth = Number(dayKey.slice(8, 10));
  const month =
    MONTHS_SHORT[lang][new Date(`${dayKey}T00:00:00Z`).getUTCMonth()];
  return `${dayOfMonth} ${month}`;
};

/**
 * Labels a page of days by its first and last day, e.g. "30 Mar – 2 Apr".
 * Collapses to a single day when the page holds only one.
 */
export const getDayRangeLabel = (firstDayKey, lastDayKey, locale) => {
  const lang = resolveLanguage(locale);
  const first = getShortDayMonth(firstDayKey, lang);
  if (firstDayKey === lastDayKey) {
    return first;
  }
  return `${first} – ${getShortDayMonth(lastDayKey, lang)}`;
};

/**
 * Formats an ISO-8601 slot timestamp as a full date:
 * "Wednesday, 15 July 2026" (en) / "miércoles, 15 de julio de 2026" (es).
 */
export const getSlotDateLabel = (iso, locale) => {
  const lang = resolveLanguage(locale);
  const dayKey = getSlotDayKey(iso);
  const date = new Date(`${dayKey}T00:00:00Z`);
  const weekday = WEEKDAYS_FULL[lang][date.getUTCDay()];
  const month = MONTHS[lang][date.getUTCMonth()];
  const dayOfMonth = Number(dayKey.slice(8, 10));
  const year = dayKey.slice(0, 4);
  return lang === 'es'
    ? `${weekday}, ${dayOfMonth} de ${month} de ${year}`
    : `${weekday}, ${dayOfMonth} ${month} ${year}`;
};

/**
 * Formats a slot day as a stacked-list heading: "Monday 28 September". No comma,
 * no year — the customer is choosing within the next days, so the year is noise.
 */
export const getSlotDateHeadingLabel = (iso, locale) => {
  const lang = resolveLanguage(locale);
  const dayKey = getSlotDayKey(iso);
  const date = new Date(`${dayKey}T00:00:00Z`);
  const weekday = WEEKDAYS_FULL[lang][date.getUTCDay()];
  const month = MONTHS[lang][date.getUTCMonth()];
  const dayOfMonth = Number(dayKey.slice(8, 10));
  return `${weekday} ${dayOfMonth} ${month}`;
};

/**
 * Groups slots by their calendar day, preserving first-seen day order. Each day
 * is { dayKey, slots }; callers format the label from the dayKey as they need.
 */
export const groupSlotsByDay = (slots) => {
  const days = [];
  (slots || []).forEach((slot) => {
    const dayKey = getSlotDayKey(slot.start);
    const existing = days.find((day) => day.dayKey === dayKey);
    if (existing) {
      existing.slots.push(slot);
      return;
    }
    days.push({ dayKey, slots: [slot] });
  });
  return days;
};
